import { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { FaChartLine } from 'react-icons/fa';
import productivityForecastApi from '../../../services/productivityForecastApi';
import { ChartCard, EmptyState, doughnutOptions } from '../analytics/analyticsShared';

const DIRECTION_STYLES = {
  improving: 'bg-green-50 text-green-700 ring-green-200',
  stable: 'bg-gray-50 text-gray-600 ring-gray-200',
  worsening: 'bg-red-50 text-red-700 ring-red-200',
};

// Admin-wide view of every real productivity forecast run across all
// farmers/devices — read-only, sourced from the same reports farmers see
// on their own AI Productivity Forecast page.
function ProductivityForecast() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await productivityForecastApi.getAll();
        setReports(res.reports || []);
      } catch (err) {
        /* interceptor toasts */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const directionChartData = useMemo(() => {
    const withData = reports.filter((r) => r.hasEnoughData);
    const colors = { improving: '#1e5c33', stable: '#6b7280', worsening: '#dc2626' };
    const keys = ['improving', 'stable', 'worsening'];
    const counts = keys.map((k) => withData.filter((r) => r.mortalityDirection === k).length);
    const entries = keys.map((k, i) => ({ k, c: counts[i] })).filter((x) => x.c > 0);
    return {
      labels: entries.map((x) => x.k.charAt(0).toUpperCase() + x.k.slice(1)),
      datasets: [{ data: entries.map((x) => x.c), backgroundColor: entries.map((x) => colors[x.k]), borderWidth: 0 }],
    };
  }, [reports]);

  const withDataCount = reports.filter((r) => r.hasEnoughData).length;

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading forecast reports…</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white">AI Productivity Forecast</h1>
        <p className="text-xs text-gray-400 mt-1">
          Every real forecast run across all farmers — a linear trend fitted to each device's own Production Records, projected 7 days forward.
        </p>
      </div>

      {reports.length === 0 ? (
        <EmptyState
          icon={<FaChartLine />}
          title="No forecasts generated yet"
          message="This fills in as farmers generate forecasts from their own Production Records (requires at least 2 real records per device)."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="text-xs text-gray-400">Total forecast runs</div>
              <div className="text-2xl font-bold text-gray-800">{reports.length}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="text-xs text-gray-400">With enough data to forecast</div>
              <div className="text-2xl font-bold text-gray-800">{withDataCount}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="text-xs text-gray-400">Insufficient data runs</div>
              <div className="text-2xl font-bold text-gray-800">{reports.length - withDataCount}</div>
            </div>
          </div>

          {directionChartData.labels.length > 0 && (
            <ChartCard title="Mortality trend direction across all forecasts" className="h-64 max-w-md">
              <div className="h-48">
                <Doughnut data={directionChartData} options={doughnutOptions} />
              </div>
            </ChartCard>
          )}

          <ChartCard title="All forecast runs">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Farmer</th>
                    <th className="py-2 pr-4">Device</th>
                    <th className="py-2 pr-4">Records used</th>
                    <th className="py-2 pr-4">Current mortality %</th>
                    <th className="py-2 pr-4">Projected (+7d)</th>
                    <th className="py-2 pr-4">Direction</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r._id} className="border-b border-gray-50">
                      <td className="py-2 pr-4 whitespace-nowrap text-gray-500">{new Date(r.createdAt).toLocaleString()}</td>
                      <td className="py-2 pr-4">{r.farmer?.firstName} {r.farmer?.lastName}</td>
                      <td className="py-2 pr-4">{r.device?.houseName || r.device?.name}</td>
                      <td className="py-2 pr-4">{r.recordCount}</td>
                      <td className="py-2 pr-4">{r.currentMortalityRatePercent ?? '—'}</td>
                      <td className="py-2 pr-4">{r.projectedMortalityRatePercent7d ?? '—'}</td>
                      <td className="py-2 pr-4">
                        {r.hasEnoughData ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${DIRECTION_STYLES[r.mortalityDirection] || DIRECTION_STYLES.stable}`}>
                            {r.mortalityDirection}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">insufficient data</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </>
      )}
    </div>
  );
}

export default ProductivityForecast;
